'use client'

import { Analytics, type BeforeSendEvent } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/next'
import Script from 'next/script'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'
import { capturePostHogPageview } from '@/utils/posthogClient'
import {
  GA_MEASUREMENT_ID,
  reconcileGoogleAnalyticsConsent,
  type GoogleAnalyticsTarget,
} from '@/utils/googleAnalytics'
import {
  filterSensitiveAnalyticsEvent,
  isSensitiveAnalyticsRoute,
} from '@/utils/analyticsUrl'

function filterAnalyticsEvent(event: BeforeSendEvent): BeforeSendEvent | null {
  return filterSensitiveAnalyticsEvent(
    event,
    typeof window === 'undefined' ? undefined : window.location.href
  )
}

interface SpeedInsightsEvent {
  type: 'vital'
  url: string
  route?: string
}

function filterSpeedInsightsEvent(
  event: SpeedInsightsEvent
): SpeedInsightsEvent | null {
  return filterSensitiveAnalyticsEvent(
    event,
    typeof window === 'undefined' ? undefined : window.location.href
  )
}

export default function OptionalAnalytics() {
  const pathname = usePathname()
  const query = useSearchParams().toString()
  const routeIsSensitive = isSensitiveAnalyticsRoute(pathname, query)
  useEffect(() => {
    reconcileGoogleAnalyticsConsent(
      routeIsSensitive ? 'denied' : 'granted',
      window as unknown as GoogleAnalyticsTarget
    )
  }, [routeIsSensitive])

  useEffect(() => {
    if (!routeIsSensitive) capturePostHogPageview()
  }, [pathname, query, routeIsSensitive])

  if (routeIsSensitive) return null

  return (
    <>
      {/* Vercel retains beforeSend after unmount, so keep the sensitive-route
          filter in the callback itself as well as at the component boundary. */}
      <Analytics beforeSend={filterAnalyticsEvent} />
      {/* Speed Insights uses the same sensitive-route filter. */}
      <SpeedInsights beforeSend={filterSpeedInsightsEvent} />
      <Script
        id="ga4-library"
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
        strategy="afterInteractive"
      />
    </>
  )
}
